import { useState, type FormEvent } from "react";
import { createCheckItem, updateCheckItem, deleteCheckItem, reorderCheckItems, fireCommand } from "../../api/commands.js";
import { TaskRow } from "../../components/tasks/TaskRow.js";
import { DragReorderList } from "../../components/ui/DragReorderList.js";
import { ViewModeToggle, type ViewMode } from "../../components/ui/ViewModeToggle.js";
import { usePersistedViewMode } from "../../components/ui/usePersistedViewMode.js";
import { useSubjectContext } from "./useSubjectContext.js";
import { Button, Checkbox, Icon, useT } from "@mmm/ui";
export function SubjectTasksTab() {
  const t = useT();
  const { subject } = useSubjectContext();
  const [draft, setDraft] = useState("");
  const [showCompleted, setShowCompleted] = useState(true);
  const [viewMode, setViewMode] = usePersistedViewMode("subject-tasks", "list" satisfies ViewMode);

  const items = subject.checkItems.slice().sort((a, b) => a.sortOrder - b.sortOrder);
  const visible = showCompleted ? items : items.filter((item) => !item.done);

  const handleAdd = async (event: FormEvent) => {
    event.preventDefault();
    if (!draft.trim()) return;
    // The form's onSubmit invokes this without awaiting it — a failed create
    // is already toasted by commands.ts's run(); keep the draft so the user
    // doesn't lose what they typed.
    try {
      await createCheckItem({ subjectId: subject.id, text: draft.trim() });
      setDraft("");
    } catch {
      // Nothing left to do.
    }
  };

  return (
    <section className="mmm-dashboard-panel mmm-task-panel mmm-task-panel--full">
      <div className="mmm-dashboard-panel-head">
        <div>
          <h2>{t("nav.tasks")}</h2>
          <p>{t("tasks.shownCount", { count: visible.length })}</p>
        </div>
        <div className="mmm-panel-actions">
          <Checkbox
            label={t("tasks.showCompleted")}
            checked={showCompleted}
            onChange={(event) => setShowCompleted(event.target.checked)}
          />
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
        </div>
      </div>

      <form onSubmit={handleAdd} className="mmm-entry-form">
        <span className="mmm-entry-form-icon">
          <Icon name="add" />
        </span>
        <input
          className="mmm-input"
          placeholder={t("tasks.addTaskPlaceholder")}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
        <Button type="submit" variant="primary">
          {t("tasks.addTask")}
        </Button>
      </form>

      <DragReorderList
        className={`mmm-task-list mmm-task-list--${viewMode}`}
        items={visible}
        onReorder={(orderedIds) => fireCommand(reorderCheckItems({ subjectId: subject.id, checkItemIds: orderedIds }))}
        renderItem={(item) => (
          <TaskRow
            item={item}
            variant={viewMode}
            onToggle={(done) => fireCommand(updateCheckItem({ checkItemId: item.id, done }))}
            onRename={(text) => updateCheckItem({ checkItemId: item.id, text })}
            onDelete={() => fireCommand(deleteCheckItem({ checkItemId: item.id }))}
          />
        )}
      />
    </section>
  );
}
